<script lang="ts">
	import { ReactiveProduct } from '$lib/state/reactiveProduct.svelte';
	import type { Stripe } from 'stripe';
	import { onDestroy } from 'svelte';
	import type { StripeProductData } from '../types';
	import log from '$lib/utils/log';
	import ProductPrice from '../parts/productPrice.svelte';
	import StarReview from '../parts/reviews/starReview.svelte';
	import type { ISbStoryData } from '@storyblok/js';
	import { editable } from '../editable';
	import Afterpay from '$lib/icons/afterpay.svelte';
	import Affirm from '$lib/icons/affirm.svelte';
	import Modal from '$lib/modal/v2/modal.svelte';
	import AffirmModalContent from '$lib/modals/bnplModal/affirmModalContent.svelte';
	import AfterpayModalContent from '$lib/modals/bnplModal/afterpayModalContent.svelte';
	import { checkoutSession } from '$lib/state/checkoutSession.svelte';
	import { fade } from 'svelte/transition';

	interface Props {
		content: ISbStoryData<{
			stripe_product: StripeProductData;
			special_promo_code: string;
			show_reviews: boolean;
			display_affirm?: boolean;
			display_afterpay?: boolean;
		}>['content'];
	}

	let { content }: Props = $props();

	let data = $derived(
		new ReactiveProduct(content.stripe_product.priceLookupKey, content.stripe_product.data)
	);

	onDestroy(() => {
		data.unsubscribe();
	});

	let specialPromoCode = $state<Stripe.PromotionCode>();

	async function fetchSpecialPromo(couponCode: string) {
		try {
			const response = await fetch('/api/stripe/promotion-codes?couponCode=' + couponCode);

			specialPromoCode = await response.json();
		} catch (e) {
			log.exception(e);

			specialPromoCode = undefined;
		}
	}

	$effect.pre(() => {
		if (!data.promotionCode && content.special_promo_code) {
			fetchSpecialPromo(content.special_promo_code);
		} else {
			specialPromoCode = undefined;
		}
	});

	let promoCode = $derived(data.promotionCode || specialPromoCode);

	let affirmModal = $state<{ show: () => void; hide: () => void }>();
	let afterpayModal = $state<{ show: () => void; hide: () => void }>();
</script>

{#snippet financingElement(type: 'affirm' | 'afterpay')}
	<button
		type="button"
		class="rounded-xl p-1.5 outline outline-2 outline-transparent hover:outline-brown-5"
		onclick={() => {
			if (type === 'affirm') {
				affirmModal?.show();
			} else {
				afterpayModal?.show();
			}
		}}
	>
		{#if type === 'affirm'}
			<Affirm class="h-4" />
		{:else}
			<Afterpay class="h-4" />
		{/if}
	</button>
{/snippet}

<div use:editable={content} class="mb-1 flex flex-col gap-2 text-[24px]/[108%] tracking-[-.24px]">
	<div class="flex flex-wrap items-center justify-between gap-x-3 gap-y-2">
		<div
			class={[
				'flex items-center gap-x-1',
				{ contents: !content.display_affirm && !content.display_afterpay }
			]}
		>
			<ProductPrice
				{promoCode}
				price={data.price.unit_amount || 99900}
				isSpecialPromo={!!specialPromoCode}
			/>

			{#if checkoutSession.canUseFinancing && (content.display_affirm || content.display_afterpay)}
				<div class="ml-2" transition:fade>
					{#if content.display_affirm}
						{@render financingElement('affirm')}
					{/if}

					{#if content.display_afterpay}
						{@render financingElement('afterpay')}
					{/if}
				</div>
			{/if}
		</div>

		{#if content.show_reviews}
			<StarReview theme="brown" />
		{/if}
	</div>
</div>

{#if checkoutSession.canUseFinancing && content.display_affirm}
	<Modal withDesktopPadding size="regular" modalName="Affirm Modal" bind:this={affirmModal}>
		<AffirmModalContent />
	</Modal>
{/if}

{#if checkoutSession.canUseFinancing && content.display_afterpay}
	<Modal withDesktopPadding size="regular" modalName="Afterpay Modal" bind:this={afterpayModal}>
		<AfterpayModalContent />
	</Modal>
{/if}
